import { Tooltip } from '@systemeio/ui'
import { getCampaignStepsEditorNextjsUrl } from 'modules/campaigns/campaign-steps/utils/get-campaign-steps-editor-nextjs-url '
import { getCampaignStepsListNextjsUrl } from 'modules/campaigns/campaign-steps/utils/get-campaign-steps-list-nextjs-url'
import { useContactCampaigns } from 'modules/contacts/contact-page/hooks/use-contact-campaigns'
import { ContactCampaignsInterface } from 'modules/contacts/contact-page/types/contact-campaigns-interface'
import React from 'react'
import LinkWithoutPrefetch from 'shared/components/link-without-prefetch'
import ListingLayout from 'shared/components/listing-layout'
import { Table } from 'shared/components/table/table'
import { ColumnDefinitionInterface } from 'shared/components/table/types/column-interface'
import { useLocoTranslation } from 'shared/hooks/use-loco-translation'
import useUser from 'shared/hooks/use-user'
import { usePagination } from 'shared/route-query/hooks/use-pagination'
import { dateFormat, dateTimeFormat } from 'shared/utils/date-time-format'
import { ContactId } from '../../types/contact-interface'

interface ContactCampaignsProps {
  contactId: ContactId
}

const ContactCampaigns = ({ contactId }: ContactCampaignsProps) => {
  const { t } = useLocoTranslation()
  const { user } = useUser()
  const { setPagination, pagination, setLimit, limit } = usePagination(true)
  const { contactCampaigns } = useContactCampaigns({ contactId, limit, pagination })

  const columns: ColumnDefinitionInterface<ContactCampaignsInterface>[] = [
    {
      key: 'campaign',
      widthPercent: 35,
      header: t('global.name'),
      onRender: campaign => (
        <div className="truncate">
          <LinkWithoutPrefetch
            className="primary-link w-full"
            href={getCampaignStepsListNextjsUrl(campaign.campaign.id)}
          >
            <Tooltip mode={'overflow'} label={campaign.campaign.name}>
              {campaign.campaign.name}
            </Tooltip>
          </LinkWithoutPrefetch>
        </div>
      ),
    },
    {
      key: 'lastCampaignStep',
      widthPercent: 35,
      header: t('dashboard.contact_page.step_name'),
      onRender: campaign =>
        campaign.lastCampaignStep ? (
          <div className="truncate">
            <LinkWithoutPrefetch
              className="primary-link w-full"
              href={getCampaignStepsEditorNextjsUrl({
                campaignId: campaign.campaign.id,
                campaignStepId: campaign.lastCampaignStep.id,
              })}
            >
              <Tooltip
                mode={'overflow'}
                label={campaign.lastCampaignStep.mailing.currentEmail.subject}
              >
                {campaign.lastCampaignStep.mailing.currentEmail.subject}
              </Tooltip>
            </LinkWithoutPrefetch>
          </div>
        ) : (
          <></>
        ),
    },
    {
      key: 'getLastStepScheduledDate',
      widthPercent: 30,
      header: t('dashboard.contact_page.last_email_sent_on'),
      onRender: campaign =>
        campaign.lastStepScheduledDate && (
          <Tooltip
            wrapperClassName={'w-full'}
            mode={'hover'}
            label={dateTimeFormat(campaign.lastStepScheduledDate, {
              timeFormat: user?.timeFormat,
              dateFormat: user?.dateFormat,
              timeZone: user?.timezone,
            })}
          >
            <span className={'w-full truncate'}>
              {dateFormat(campaign.lastStepScheduledDate, {
                dateFormat: user?.dateFormat,
                timeZone: user?.timezone,
              })}
            </span>
          </Tooltip>
        ),
      onRenderSmall: campaign =>
        campaign.lastStepScheduledDate && (
          <div className={'flex items-center gap-3'}>
            <span className={'text-text-primary font-bold'}>{t('global.date')}</span>
            <span>
              {dateTimeFormat(campaign.lastStepScheduledDate, {
                timeFormat: user?.timeFormat,
                dateFormat: user?.dateFormat,
                timeZone: user?.timezone,
              })}
            </span>
          </div>
        ),
    },
  ]
  return (
    <ListingLayout
      counter={false}
      data={contactCampaigns}
      type={'compact'}
      title={
        <h2 className={'h-[42px] text-[17px] font-bold'}>
          {t('dashboard.contact_page.email_campaigns')}
        </h2>
      }
    >
      <Table
        limiter={{ limit, setLimit }}
        paginator={{ pagination, setPagination }}
        data={contactCampaigns}
        columns={columns}
      />
    </ListingLayout>
  )
}

export default ContactCampaigns
